import {Component, effect, inject, OnDestroy, untracked, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {AuthService} from '../services/auth.service';
import {ApiService} from '../services/api.service';
import {SettingsService, utcOffsetValue} from '../services/settings.service';
import {CSV_DOWNLOAD_HINT, DataService} from '../services/data.service';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatPaginator, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {FilterBarComponent} from '../shared/filter-bar/filter-bar.component';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatIcon} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {UnProcessedSoldVin} from '../models/soldUnprocessed/unProcessedSoldVin.model';
import {toObservable} from '@angular/core/rxjs-interop';
import { skip } from 'rxjs/operators';
import { DataFilterService } from '../services/data-filter.service';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SoldUnprocessedCounts } from '../models/landingPage/soldUnprocessedCounts.model';
import { latestLoad } from '../shared/latest-load';

type DateRange = { start: Date; end: Date };

/** One table request: the range, and the cursor for the page wanted. */
interface TableLoad {
  dateRange: DateRange;
  cursor: string | null;
}

@Component({
  selector: 'app-unprocessed-sold',
  imports: [
    FilterBarComponent,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule,
    MatIcon,
    MatButtonModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './unprocessed-sold.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './unprocessed-sold.component.scss'
})
export class UnprocessedSoldComponent implements OnDestroy {
  readonly auth = inject(AuthService);
  readonly api = inject(ApiService);
  private readonly settings = inject(SettingsService);
  private readonly filters = inject(DataFilterService);
  readonly data = inject(DataService);
  tableLoading: boolean = false;

  readonly csvDownloadHint = CSV_DOWNLOAD_HINT;

  readonly displayedColumns = [
    'saleDate',
    'status',
    'vinCode',
    'decodedMake',
    'decodedModel',
    'decodedYear',
    'processorSite',
    'username',
    'controllerName',
    'controllerId',
    'classification'
  ];

  readonly tableHeaderTooltips = {
    saleDate: "Date when the vehicle was sold",
    status: "Processing Status",
    vinCode: "17-digit vehicle ID",
    decodedMake: "Vehicle manufacturer",
    decodedModel: "Vehicle model",
    decodedYear: "Vehicle manufacturing year",
    processorSite: "Name of the site processing the vehicle",
    username: "",
    controllerName: "Name of the company who owns the vehicle",
    controllerId: "ID of the company who owns the vehicle",
    classification: "Vehicle inventory processing category"
  }

  dataSource = new MatTableDataSource<UnProcessedSoldVin>([]);

  soldUnprocessedCounts: SoldUnprocessedCounts | undefined;

  soldUnprocessedCountsLoading: boolean = false;

  /** Table loads — only the latest counts; see latestLoad. */
  private readonly loadTable = latestLoad<TableLoad>(
    ({ dateRange, cursor }) => this.api.getSoldUnProcessedVinsPaginated(
      toIsoDate(dateRange.start),
      toIsoDate(dateRange.end),
      utcOffsetValue(this.settings.timezone(), dateRange.start),
      cursor,
      this.data.scanSoldUnprocessedVinTablePagination().pageSize,
      true // the report reflects the user's selection
    ),
    active => this.tableLoading = active
  );

  /**
   * Counts loads. They reload on every timezone change, and a closed timezone
   * select changes value on each arrow-key press — so several can be in flight
   * at once, and only the latest may land.
   */
  private readonly loadSoldUnprocessedCounts = latestLoad<string>(
    timezone => this.api.getSoldUnprocessedCounts(utcOffsetValue(timezone)),
    active => this.soldUnprocessedCountsLoading = active
  );

  @ViewChild(MatPaginator) private paginator!: MatPaginator;

  constructor() {

    toObservable(this.data.soldUnprocessedCounts).subscribe(soldUnprocessedCounts => {
      this.soldUnprocessedCounts = soldUnprocessedCounts;
    })

    toObservable(this.data.newScansSoldVin).subscribe(newScans => {
      this.dataSource.data = newScans;
    })

    toObservable(this.data.dateRange).subscribe(dateRange => this.reload(dateRange));

    // Filters are request inputs, so a change has to re-request. skip(1): the
    // initial load is driven by dateRange, and replaying the current filters on
    // mount would double it. Later emissions are always real changes — the
    // applied signal's equality suppresses re-applying an unchanged selection.
    toObservable(this.filters.applied).pipe(skip(1))
      .subscribe(() => this.reload(this.data.dateRange()));

    toObservable(this.settings.timezone).subscribe(timezone => {

      if (this.settings.defaultTimezoneInitialized == true) {
        this.reload(this.data.dateRange());
      } else {
        this.settings.defaultTimezoneInitialized = true;
      }

    })

    this.dataSource.paginator = this.paginator;

    effect(() => {

      const timezone = this.settings.timezone();

      untracked(() => this.loadSoldUnprocessedCounts(timezone));

    });

  }

  ngOnDestroy(): void {
    this.settings.defaultTimezoneInitialized = false;
  }

  paginatorEventHandler(ev: PageEvent) {
    if (ev.previousPageIndex && ev.pageIndex < ev.previousPageIndex) {
      this.loadTableData(this.data.dateRange(), false, false, true);
    } else {
      this.loadTableData(this.data.dateRange(), false, true, false);
    }
  }

  /** Back to the first page, then reload the table. */
  private reload(dateRange: DateRange | null): void {
    // Left exactly as before: the three report paginators currently share one
    // object, so this reset must not change on its own.
    this.data.setDefaultScanTablePagination();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadTableData(dateRange, true, false, false);
  }

  loadTableData(dateRange: DateRange | null, refresh: boolean, next: boolean, previous: boolean) {

    if (dateRange == null) {
      return;
    }

    const pagination = this.data.scanSoldUnprocessedVinTablePagination();

    this.dataSource.data = [];

    this.loadTable({
      dateRange,
      cursor: refresh ? null : next ? pagination.cursorNext : pagination.cursorPrev
    });

  }

  formatDateTime(iso: string): string {
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'medium',
      /* timeStyle: 'short', */
      timeZone: this.settings.timezone()
    }).format(new Date(iso));
  }

  exportUnprocessedSoldVinReportCSV() {

    const dateRange = this.data.dateRange();

    if (dateRange != null) {
      this.api.exportUnprocessedSoldVinReportCSV(
        toIsoDate(dateRange.start),
        toIsoDate(dateRange.end),
        utcOffsetValue(this.settings.timezone(), dateRange.start),
        true // the export matches the table on screen
      );
    }

  }

}
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
